import { create } from "zustand"
import { persist } from "zustand/middleware"
import { useCommunicationStore } from "./communicationStore"

type SettingsStore = {
	selectedMicrophone: string | undefined
	selectedSpeaker: string | undefined
	selectedCamera: string | undefined
	microphones: MediaDeviceInfo[]
	speakers: MediaDeviceInfo[]
	cameras: MediaDeviceInfo[]
	updateIO: (devices: MediaDeviceInfo[]) => void
	updateMicrophone: (id: string) => void
	updateSpeaker: (id: string) => void
	updateCamera: (id: string) => void

	debug: boolean
	updateDebug: (isDebugEnabled: boolean) => void

	isNameTagEnabled: boolean
	updateIsNameTagEnabled: (isNameTagEnabled: boolean) => void
}

const updateDevice = (devices: MediaDeviceInfo[], selected: string | unknown, update: (id: string) => void) => {
	if (devices.length > 0) {
		if (selected === undefined) {
			// prefer devices including "default"
			const defaultDevices = devices.filter((device) => device.deviceId === "default")[0]
			update(defaultDevices !== undefined ? defaultDevices.deviceId : devices[0].deviceId)
		} else {
			// already has a device but ids get flushed out the cache at some point
			const microphone = devices.filter((device) => device.deviceId === selected)
			if (microphone.length === 0) {
				update(devices[0].deviceId)
			}
		}
	}
}

export const useSettingsStore = create(
	persist<SettingsStore>(
		(set, get) => ({
			selectedMicrophone: undefined,
			selectedSpeaker: undefined,
			selectedCamera: undefined,
			microphones: [],
			speakers: [],
			cameras: [],

			updateIO: (devices) => {
				const microphones = devices.filter((device) => device.kind === "audioinput")
				const speakers = devices.filter((device) => device.kind === "audiooutput")
				const cameras = devices.filter((device) => device.kind === "videoinput")

				set({
					microphones,
					speakers,
					cameras,
				})

				const {
					selectedMicrophone,
					selectedSpeaker,
					selectedCamera,
					updateMicrophone,
					updateSpeaker,
					updateCamera,
				} = get()

				updateDevice(microphones, selectedMicrophone, updateMicrophone)
				updateDevice(cameras, selectedCamera, updateCamera)
				updateDevice(speakers, selectedSpeaker, updateSpeaker)
			},
			updateMicrophone: (id) => {
				set({ selectedMicrophone: id })

				useCommunicationStore.getState().ioActions.updateMicrophone(id)
			},
			updateSpeaker: (id) => {
				set({ selectedSpeaker: id })
				useCommunicationStore.getState().ioActions.updateSpeaker(id)
			},
			updateCamera: (id) => {
				set({ selectedCamera: id })

				useCommunicationStore.getState().ioActions.updateCamera(id)
			},

			debug: false,
			updateDebug(isDebugEnabled) {
				set({ debug: isDebugEnabled })
			},

			isNameTagEnabled: true,
			updateIsNameTagEnabled(isNameTagEnabled) {
				set({ isNameTagEnabled: isNameTagEnabled })
			},
		}),
		{ name: "settings" }
	)
)
